import React, { useEffect, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface SkuRow {
    sl_no: number;
    product_name: string;
    pack_size: string;
    quantity: number;
    total_value: number;
    ctn_pail_drum_quantity: number;
    free_product: number;
    total: number;
    total_ctn_pail_drum_quantity: number;
}

interface SkuCategoryGroup {
    category_name: string;
    category_code: string;
    products: SkuRow[];
    subtotal: Omit<SkuRow, 'sl_no' | 'product_name' | 'pack_size'>;
}

interface SkuWiseSalesData {
    categories: SkuCategoryGroup[];
    grand_total: Omit<SkuRow, 'sl_no' | 'product_name' | 'pack_size'>;
    period_label: string;
}

const SkuWiseSalesReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<SkuWiseSalesData | null>(null);
    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? String(projectObj.value) : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setFormData((prev) => ({
            ...prev,
            idProjects: selected ? String(selected.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (formData.fromDate > formData.toDate) {
            toast.error('To Date must be after or equal to From Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'mis-reports/sku-wise-sales-report',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.data);
            } else {
                setReportData(null);
                toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const renderTotalCells = (totals: Omit<SkuRow, 'sl_no' | 'product_name' | 'pack_size'>, isBold = false) => (
        <>
            <td className="text-end">{isBold ? <strong>{numberFormat(totals.quantity)}</strong> : numberFormat(totals.quantity)}</td>
            <td className="text-end">{isBold ? <strong>{numberFormat(totals.total_value)}</strong> : numberFormat(totals.total_value)}</td>
            <td className="text-end">{isBold ? <strong>{numberFormat(totals.ctn_pail_drum_quantity)}</strong> : numberFormat(totals.ctn_pail_drum_quantity)}</td>
            <td className="text-end">{isBold ? <strong>{numberFormat(totals.free_product)}</strong> : numberFormat(totals.free_product)}</td>
            <td className="text-end">{isBold ? <strong>{numberFormat(totals.total)}</strong> : numberFormat(totals.total)}</td>
            <td className="text-end">{isBold ? <strong>{numberFormat(totals.total_ctn_pail_drum_quantity)}</strong> : numberFormat(totals.total_ctn_pail_drum_quantity)}</td>
        </>
    );

    const stickyHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: 1022,
        backgroundColor: '#f8f9fa',
        boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span><i className='fas fa-eye'></i> View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    {reportData ? (
                        <>
                            <div className="mb-3">
                                <strong>Period:</strong> {reportData.period_label}
                            </div>
                            <div style={{
                                maxHeight: 'calc(100vh - 300px)',
                                overflowY: 'auto',
                                overflowX: 'auto',
                                position: 'relative',
                            }}>
                                <Table bordered hover size="sm" style={{ marginBottom: 0, width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={stickyHeaderStyle}>SL No</th>
                                            <th style={stickyHeaderStyle}>Product Name</th>
                                            <th style={stickyHeaderStyle}>Pack Size</th>
                                            <th className="text-end" style={stickyHeaderStyle}>Quantity</th>
                                            <th className="text-end" style={stickyHeaderStyle}>Total Value</th>
                                            <th className="text-end" style={stickyHeaderStyle}>CTN/Pail/Drum Quantity</th>
                                            <th className="text-end" style={stickyHeaderStyle}>Free Product</th>
                                            <th className="text-end" style={stickyHeaderStyle}>Total</th>
                                            <th className="text-end" style={stickyHeaderStyle}>Total CTN/Pail/Drum Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.categories.map((category) => (
                                            <React.Fragment key={category.category_name}>
                                                {category.products.map((row) => (
                                                    <tr key={`${category.category_name}-${row.sl_no}`}>
                                                        <td>{row.sl_no}</td>
                                                        <td>{row.product_name}</td>
                                                        <td>{row.pack_size}</td>
                                                        <td className="text-end">{numberFormat(row.quantity)}</td>
                                                        <td className="text-end">{numberFormat(row.total_value)}</td>
                                                        <td className="text-end">{numberFormat(row.ctn_pail_drum_quantity)}</td>
                                                        <td className="text-end">{numberFormat(row.free_product)}</td>
                                                        <td className="text-end">{numberFormat(row.total)}</td>
                                                        <td className="text-end">{numberFormat(row.total_ctn_pail_drum_quantity)}</td>
                                                    </tr>
                                                ))}
                                                <tr className="table-secondary">
                                                    <td colSpan={3}>
                                                        <strong>{category.category_code || category.category_name} Total</strong>
                                                    </td>
                                                    {renderTotalCells(category.subtotal, true)}
                                                </tr>
                                            </React.Fragment>
                                        ))}
                                        {reportData.categories.length > 0 && (
                                            <tr>
                                                <td colSpan={3} className="text-end">
                                                    <strong>Grand Total</strong>
                                                </td>
                                                {renderTotalCells(reportData.grand_total, true)}
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    ) : (
                        <div>No data available</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default SkuWiseSalesReport;
